import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { COLLECTIONS } from '../platform/constants';
import { markItemAsRead as markItemAsReadService } from '../platform/lastRead/lastReadService';
import { useUser } from './UserContext';

const LastReadContext = createContext(null);

export function LastReadProvider({ children }) {
  const { user, loading: userLoading } = useUser();
  const [lastRead, setLastRead] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setLastRead({});
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const userRef = doc(db, COLLECTIONS.USERS, user.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        setLastRead(snapshot.data()?.lastRead || {});
        setLoading(false);
      },
      (error) => {
        console.error('Error subscribing to user lastRead:', error);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [user?.uid]);

  const markItemAsRead = useCallback(async (module, itemKey) => {
    if (!user?.uid) return;
    await markItemAsReadService(user.uid, module, itemKey);
  }, [user?.uid]);

  const value = useMemo(
    () => ({
      lastRead,
      loading: userLoading || loading,
      markItemAsRead,
    }),
    [lastRead, userLoading, loading, markItemAsRead],
  );

  return (
    <LastReadContext.Provider value={value}>
      {children}
    </LastReadContext.Provider>
  );
}

export function useLastRead() {
  const context = useContext(LastReadContext);
  if (!context) {
    throw new Error('useLastRead must be used within LastReadProvider');
  }
  return context;
}

export function useLastReadModule(module) {
  const { lastRead, loading, markItemAsRead } = useLastRead();
  const lastReadMap = lastRead[module] || {};

  const markModuleItemAsRead = useCallback(
    (itemKey) => markItemAsRead(module, itemKey),
    [markItemAsRead, module],
  );

  return {
    lastReadMap,
    loading,
    markItemAsRead: markModuleItemAsRead,
  };
}
